import { Control, FieldValues, Path, useController } from 'react-hook-form';
import styled, { css } from 'styled-components';

import Text from '@/components/Text';

const Grid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

interface OptionButtonProps {
  $active: boolean;
  $inline?: boolean;
}

const OptionButton = styled.button.attrs({ type: 'button' })<OptionButtonProps>`
  padding: 12px 16px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  display: flex;
  align-items: center;
  gap: 8px;
  transition: 0.15s ease-out border-color;

  &:hover {
    border-color: ${({ theme }) => theme.colors.purple};
  }

  ${({ $active, theme }) =>
    $active &&
    css`
      border-color: ${theme.colors.purple};
    `}

  ${({ $inline = false, theme }) =>
    $inline &&
    css`
      display: flex;
      column-gap: 32px;
      row-gap: 16px;
      &:hover {
        border-color: ${theme.colors.grayscale};
      }
    `}
`;

const OptionCircle = styled.div<{ $active: boolean }>`
  border-radius: 50%;
  width: 16px;
  height: 16px;
  border: 2px solid ${({ theme }) => theme.colors.gray};
  position: relative;
  transition: 0.15s ease-out border-color;

  &::after {
    content: ' ';
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    margin: auto;
    transition: 0.15s ease-out background-color;
    background-color: transparent;
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }

  ${({ $active, theme }) =>
    $active &&
    css`
      border-color: ${theme.colors.purple};
      &::after {
        background-color: ${theme.colors.purple};
      }
    `}
`;

const InlineOption = styled.button.attrs({ type: 'button' })`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export interface RadioOption<OptionValueT = number> {
  value: OptionValueT;
  label: string;
}

interface Props<T extends FieldValues, OptionValueT = number> {
  control: Control<T>;
  name: Path<T>;
  options: RadioOption<OptionValueT>[];
  inline?: boolean;
}

const RadioField = <T extends FieldValues, OptionValueT = number>({
  control,
  name,
  options,
  inline = false,
}: Props<T, OptionValueT>) => {
  const {
    field: { onChange, value },
  } = useController({
    name,
    control,
  });

  if (inline) {
    return (
      <OptionButton as="div" $active={false} $inline>
        {options.map((option, i) => (
          <InlineOption key={i} onClick={() => onChange(option.value)}>
            <OptionCircle $active={option.value === value} />
            <Text as="span" $type="caption" $color="black">
              {option.label}
            </Text>
          </InlineOption>
        ))}
      </OptionButton>
    );
  }

  return (
    <Grid>
      {options.map((option, i) => (
        <OptionButton
          $active={option.value === value}
          key={i}
          onClick={() => onChange(option.value)}
        >
          <OptionCircle $active={option.value === value} />
          <Text as="span" $type="caption" $color="black">
            {option.label}
          </Text>
        </OptionButton>
      ))}
    </Grid>
  );
};

export default RadioField;
